import React from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import styled from 'styled-components';
import { get, post, useStandardState } from '../../../utility/api';
import { setCookie, deleteCookie } from '../../../utility/cookies';
import { SessionProvider, useFitToViewport } from '../../../utility/hooks';
import Text from '../../shared/Text';
import Spinner from '../../shared/Spinner';
import ScreenWrapper from '../../shared/ScreenWrapper';
import Header from './Header';
import Body from './Body';
import CodeSection from './CodeSection';
import LeaderBoardModal from './LeaderBoardModal';
import styles from '../../../utility/styles';
const INTERVAL = 5000;
// Smallest canvas (CSS px) the fixed-px dash layout fits in; smaller windows zoom the dash down.
const DESIGN_WIDTH = 1280;
const DESIGN_HEIGHT = 1040;

const StyledContainer = styled(Container)`
    height: calc(100vh / var(--dash-scale, 1));
    display: flex;
    flex-direction: column;
    justify-content: ${({ justify }) => justify || 'space-between'};
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

export default function WebDash({ session_id }) {
    useFitToViewport(DESIGN_WIDTH, DESIGN_HEIGHT);

    const [{ data, error, isFetching }, { startFetching, recieveData, recieveError }] = useStandardState();
    const [{ data: fsdData, error: fsdError, isFetching: fsdIsFetching }, { startFetching: startFsdFetching, recieveData: recieveFsdData, recieveError: recieveFsdError }] = useStandardState();
    const [initalLoading, setInitialLoading] = React.useState(true);
    const loadInterval = React.useRef(null);

    // FSD Leaderboard
    const [leaderboardType, setLeaderboardType] = React.useState('distance');
    const [leaderboardPeriod, setLeaderboardPeriod] = React.useState('days7');
    const [leaderboardAutopilotType, setLeaderboardAutopilotType] = React.useState('fsd');

    const [leaderboardOpen, setLeaderboardOpen] = React.useState(false);

    // Open and close leaderboard
    const openLeaderboard = React.useCallback(() => {

        setLeaderboardOpen(true);
    }, []);

    const closeLeaderboard = React.useCallback(() => {
        setLeaderboardOpen(false);
    }, []);

    const loadFsdData = async () => {
        if (fsdIsFetching) return
        try {
            startFsdFetching();
            const { data: newFsdData, error: newFsdError } = await post(`/dash/${session_id}/leaderboard/fsd`, {
                type: leaderboardType,
                period: leaderboardPeriod,
                autopilot_type: leaderboardAutopilotType
            });
            if (newFsdError) throw newFsdError;
            if (newFsdData) recieveFsdData(newFsdData)
        } catch (error) {
            recieveFsdError(typeof error === 'string' ? error : "Yipes! We ran into an issue. Hang tight a few seconds and we'll try again.");
        }
    }

    const refreshFsdData = async (type, period, autopilot_type) => {
        if (fsdIsFetching) return;
        try {
            startFsdFetching();
            const { data: newFsdData, error: newFsdError } = await post(`/dash/${session_id}/leaderboard/fsd`, {
                type,
                period,
                autopilot_type
            });
            if (newFsdError) throw newFsdError;
            if (newFsdData) recieveFsdData(newFsdData)
        } catch (error) {
            recieveFsdError(typeof error === 'string' ? error : "Yipes! We ran into an issue. Hang tight a few seconds and we'll try again.");
        }
    }

    const loadData = React.useCallback(async () => {
        if (isFetching) return
        try {
            startFetching();
            const { data, error } = await get(`/v2/dash/${session_id}/data`);
            if (error) {
                // Check if it's a 401 error
                if (error.includes('401') || error.includes('Unauthorized')) {
                    await deleteCookie('wdsid');
                    window.location.replace(window.location.origin + window.location.pathname);
                    return;
                }
                throw error;
            }
            if (data) recieveData(data)
            setInitialLoading(false);
        } catch (error) {
            recieveError(typeof error === 'string' ? error : "Yipes! We ran into an issue. Hang tight a few seconds and we'll try again.");
            setInitialLoading(false);
        }
    }, [isFetching, startFetching, recieveData, recieveError, setInitialLoading]);

    React.useEffect(() => {
        loadData();
        loadInterval.current = setInterval(loadData, INTERVAL)
        return () => clearInterval(loadInterval.current)
    }, []);

    React.useEffect(() => {
        const saveSessionId = async () => {
            await setCookie('wdsid', session_id, 30); // Store session ID in cookie for 30 days
        };
        saveSessionId();
    }, [session_id])

    React.useEffect(() => {
        if (session_id) loadFsdData();
    }, [session_id]);

    React.useEffect(() => {
        if (fsdData) {
            setLeaderboardType(fsdData.type);
            setLeaderboardPeriod(fsdData.period);
            setLeaderboardAutopilotType(fsdData.autopilot_type);
        }
    }, [fsdData]);

    if (!data?.authenticated || initalLoading) {
        return (
            <SessionProvider {...{ session_id }}>
                <ScreenWrapper
                    noFooter
                    justify="center"
                >
                    <StyledContainer>
                        <Row>
                            <Col>
                                {initalLoading ? (
                                    <LoadingContainer>
                                        <Text.Heading style={{ marginRight: '24px' }}>Loading</Text.Heading>
                                        <Spinner color={styles.colors.white} size={40} />
                                    </LoadingContainer>
                                ) : (
                                    <CodeSection {...{ error, isFetching }} />
                                )}
                            </Col>
                        </Row>
                    </StyledContainer>
                </ScreenWrapper>
            </SessionProvider>
        )
    }

    return (
        <SessionProvider {...{ session_id }}>
            <StyledContainer fluid>
                <Header booleans={{ is_driving: data.is_driving, is_charging: data.is_charging }} status={data.vehicle_status} lastFetched={data.timestamp} openLeaderboard={openLeaderboard} />
                <Body booleans={{ is_driving: data.is_driving, is_charging: data.is_charging }} status={data.vehicle_status} {...{ data }} />
                <LeaderBoardModal
                    isOpen={leaderboardOpen}
                    onClose={closeLeaderboard}
                    type={leaderboardType}
                    period={leaderboardPeriod}
                    autopilot_type={leaderboardAutopilotType}
                    leaderboardData={fsdData?.leaderboard || []}
                    isLoading={fsdIsFetching}
                    onTypeChange={setLeaderboardType}
                    onPeriodChange={setLeaderboardPeriod}
                    onAutopilotTypeChange={setLeaderboardAutopilotType}
                    onFetchData={refreshFsdData}
                />
            </StyledContainer>
        </SessionProvider>
    )
}
